import React, { useState, useEffect } from "react";
import { MagnifyingGlassIcon, UserGroupIcon, ClockIcon, ArrowUpIcon, SparklesIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import axios from 'axios';
import { ClubCardSkeleton, SkeletonGrid } from './components/SkeletonLoader';
import api from './api';

const API = "https://dvtime-1c6x.onrender.com";

// Custom hook for hide-on-scroll behavior on mobile
function useHideOnScroll() {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Only apply on mobile (screens smaller than 768px)
      if (window.innerWidth >= 768) {
        setIsVisible(true);
        return;
      }

      // Show header when scrolling up, hide when scrolling down
      if (currentScrollY < lastScrollY || currentScrollY < 50) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return isVisible;
}

function ClubCard({ club, onSelect, isJoined }) {
  return (
    <div 
      onClick={() => onSelect(club)}
      className="rounded-xl shadow-md transition-all duration-300 p-5 cursor-pointer border-2 bg-white dark:bg-gray-900 border-transparent hover:border-gray-200 dark:hover:border-gray-700 hover:shadow-xl relative"
    >
      {isJoined && (
        <div className="absolute top-3 right-3">
          <CheckCircleIconSolid className="h-6 w-6 text-green-500" />
        </div>
      )}
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 pr-8">{club['Club Name']}</h2>
      
      <div className="space-y-2 mb-3">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          <strong className="text-gray-900 dark:text-white">President:</strong> {club['President Name'] || 'TBD'}
        </p>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          <strong className="text-gray-900 dark:text-white">Advisor:</strong> {club['Advisor '] || 'TBD'}
        </p>
        <p className="text-sm text-indigo-600 dark:text-indigo-400 font-medium flex items-center gap-1">
          <ClockIcon className="h-4 w-4" />
          {club['Meeting Frequency'] || 'Schedule TBD'}
        </p>
      </div>
      
      {club['Club Description'] && (
        <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed line-clamp-3">
          {club['Club Description']}
        </p>
      )}
    </div>
  );
}

export default function Clubs() {
  const [clubs, setClubs] = useState([]);
  const [filteredClubs, setFilteredClubs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [advisorFilter, setAdvisorFilter] = useState('');
  const [sortBy, setSortBy] = useState('alpha_asc');
  const [loading, setLoading] = useState(true);
  const [modalClub, setModalClub] = useState(null);
  const [joinedClubs, setJoinedClubs] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [clubsPerPage] = useState(12);
  const [totalPages, setTotalPages] = useState(0);
  
  // Hide-on-scroll for mobile
  const isHeaderVisible = useHideOnScroll();

  useEffect(() => {
    fetchClubs();
    fetchJoinedClubs();
  }, []);

  const fetchJoinedClubs = async () => {
    try {
      const response = await api.get('/user/clubs');
      setJoinedClubs(response.data.joined_clubs || []);
    } catch (error) {
      console.error('Error fetching joined clubs:', error);
      setJoinedClubs([]);
    }
  };

  const toggleJoinClub = async (clubId) => {
    try {
      const isJoined = joinedClubs.includes(clubId);
      const endpoint = isJoined ? `/user/clubs/leave/${clubId}` : `/user/clubs/join/${clubId}`;
      
      const response = await api.post(endpoint);
      setJoinedClubs(response.data.joined_clubs || []);
      
      // Dispatch custom event to notify Dashboard
      window.dispatchEvent(new Event('clubsUpdated'));
    } catch (error) {
      console.error('Error toggling club membership:', error);
      alert('Failed to update club membership. Please try again.');
    }
  };

  useEffect(() => {
    filterClubs();
  }, [clubs, searchTerm, advisorFilter, sortBy, currentPage]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, advisorFilter, sortBy]);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && modalClub) {
        setModalClub(null);
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [modalClub]);

  const fetchClubs = async () => {
    try {
      const response = await axios.get(`${API}/clubs`);
      setClubs(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching clubs:', error);
      setLoading(false);
    }
  };

  const filterClubs = () => {
    let filtered = clubs;

    if (searchTerm) {
      filtered = filtered.filter(club =>
        club['Club Name'].toLowerCase().includes(searchTerm.toLowerCase()) ||
        (club['Club Description'] && club['Club Description'].toLowerCase().includes(searchTerm.toLowerCase())) ||
        (club['President Name'] && club['President Name'].toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (advisorFilter) {
      filtered = filtered.filter(club =>
        club['Advisor '] && club['Advisor '].toLowerCase().includes(advisorFilter.toLowerCase())
      );
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      const nameA = a['Club Name'] || '';
      const nameB = b['Club Name'] || '';
      
      switch (sortBy) {
        case 'alpha_asc':
          return nameA.localeCompare(nameB);
        case 'alpha_desc':
          return nameB.localeCompare(nameA);
        default:
          return 0;
      }
    });

    // Calculate pagination
    const calculatedTotalPages = Math.ceil(filtered.length / clubsPerPage);
    const startIndex = (currentPage - 1) * clubsPerPage;
    const endIndex = startIndex + clubsPerPage;
    const paginatedClubs = filtered.slice(startIndex, endIndex);

    setTotalPages(calculatedTotalPages);
    setFilteredClubs(paginatedClubs);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-900 rounded-xl p-6 animate-pulse">
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4"></div>
                <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className={`sticky top-16 z-20 bg-gray-50/95 dark:bg-gray-950/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/70 dark:supports-[backdrop-filter]:bg-gray-950/70 border-b border-gray-200 dark:border-gray-800/70 transition-transform duration-300 ${isHeaderVisible ? 'translate-y-0' : 'md:translate-y-0 -translate-y-full'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Header */}
          <div className="mb-4">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">DVHS Clubs</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">Discover and join student organizations.</p>
          </div>
          
          {/* Search and Filter */}
          <div className="flex flex-wrap items-center gap-3 md:gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[240px]">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3 pointer-events-none" />
              <input
                type="text"
                placeholder="Search clubs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-800 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 transition-all"
              />
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2">
              <ArrowUpIcon className="h-4 w-4 text-gray-400" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-800 rounded-lg text-sm text-gray-900 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500 transition-all"
              >
                <option value="alpha_asc">A → Z</option>
                <option value="alpha_desc">Z → A</option>
              </select>
            </div>

            {/* Advisor Filter */}
            <input
              type="text"
              placeholder="Filter by advisor..."
              value={advisorFilter}
              onChange={(e) => setAdvisorFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 dark:border-gray-800 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 transition-all"
            />
            
            {/* Clear Filters Button */}
            {(searchTerm || advisorFilter || sortBy !== 'alpha_asc') && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setAdvisorFilter('');
                  setSortBy('alpha_asc');
                }}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Club Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <SkeletonGrid count={12} Component={ClubCardSkeleton} />
        ) : filteredClubs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClubs.map((club, idx) => (
              <ClubCard 
                key={idx} 
                club={club} 
                onSelect={setModalClub}
                isJoined={joinedClubs.includes(club['Club Name'])}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="mb-6 relative">
              <UserGroupIcon className="h-24 w-24 text-gray-300 dark:text-gray-600 mx-auto" />
              <div className="absolute inset-0 flex items-center justify-center">
                <SparklesIcon className="h-12 w-12 text-indigo-400 opacity-50 animate-pulse" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No clubs found</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">Try adjusting your search or filter criteria</p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSortBy('alpha_asc');
                setAdvisorFilter('');
              }}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center mt-8 space-x-2">
            <button
              onClick={() => {
                setCurrentPage(prev => Math.max(prev - 1, 1));
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-xl bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
            >
              Previous
            </button>
            
            {/* Page Numbers */}
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
              const showPage = page === 1 || page === totalPages || 
                              (page >= currentPage - 2 && page <= currentPage + 2);
              
              if (!showPage) {
                if (page === currentPage - 3 || page === currentPage + 3) {
                  return <span key={page} className="px-2 text-gray-400 dark:text-gray-500">...</span>;
                }
                return null;
              }
              
              return (
                <button
                  key={page}
                  onClick={() => {
                    setCurrentPage(page);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`px-4 py-2 rounded-xl transition-all font-medium ${
                    currentPage === page
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            
            <button
              onClick={() => {
                setCurrentPage(prev => Math.min(prev + 1, totalPages));
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-xl bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Modal for club details */}
      {modalClub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-all duration-200" onClick={() => setModalClub(null)}>
          <div
            className="relative bg-white/95 backdrop-blur-md dark:bg-gray-900/95 rounded-2xl shadow-lg max-w-2xl w-full mx-4 p-6 border border-gray-200/50 dark:border-gray-700/50 max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl font-bold focus:outline-none"
              onClick={() => setModalClub(null)}
              aria-label="Close"
            >
              &times;
            </button>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pr-8">{modalClub['Club Name']}</h2>
            
            <div className="space-y-3 mb-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong className="text-gray-900 dark:text-white">President:</strong> {modalClub['President Name'] || 'TBD'}
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong className="text-gray-900 dark:text-white">Advisor:</strong> {modalClub['Advisor '] || 'TBD'}
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2">
                <ClockIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
                <span><strong className="text-gray-900 dark:text-white">Meeting:</strong> {modalClub['Meeting Frequency'] || 'Schedule TBD'}</span>
              </p>
              {modalClub['Best Point of Contact'] && (
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong className="text-gray-900 dark:text-white">Contact:</strong>{' '}
                  <a href={`mailto:${modalClub['Best Point of Contact']}`} className="text-indigo-600 dark:text-indigo-400 hover:underline">
                    {modalClub['Best Point of Contact']}
                  </a>
                </p>
              )}
            </div>
            
            {modalClub['Club Description'] && (
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">About</h3>
                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-line">
                  {modalClub['Club Description']}
                </p>
              </div>
            )}
            
            {/* Membership Toggle */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <label className="flex items-center justify-between cursor-pointer group">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Club Membership</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Mark if you're a member of this club.</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleJoinClub(modalClub['Club Name']);
                  }}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                    joinedClubs.includes(modalClub['Club Name'])
                      ? 'bg-green-500'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                      joinedClubs.includes(modalClub['Club Name']) ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
